<template>
  <div>
    <v-app-bar color="primary" flat app dark>
      <v-app-bar-nav-icon style="position: absolute" @click="goBack">
        <v-icon size="large" color="white">mdi-chevron-left</v-icon>
      </v-app-bar-nav-icon>
      <v-spacer />
      <v-app-bar-title style="min-width: fit-content; font-size: 14px;">
        {{ $t('aiReport.title') }}
      </v-app-bar-title>
      <v-spacer />
    </v-app-bar>

    <v-main>
      <v-progress-linear v-if="isFetching" indeterminate color="primary" />

      <div v-if="detail" class="pa-3">
        <div class="d-flex align-center" style="gap: 0.5rem;">
          <span class="text-h6 font-weight-bold">
            {{ detail.lastName }} {{ detail.firstName }}
          </span>
          <ai-report-status-chip :status="detail.status" />
        </div>
        <div class="text-caption text-grey">
          {{ $t('aiReport.detail.subtitle', { className: detail.className, ageLabel: detail.ageLabel, year: detail.businessYear, monthsLabel: detail.monthsLabel }) }}
        </div>

        <v-alert
          v-if="detail.dataCoverageMonths.length < 3"
          type="warning"
          variant="outlined"
          density="compact"
          class="mt-3"
        >
          {{ $t('aiReport.detail.coverageWarning', { months: formatCoverage(detail.dataCoverageMonths) }) }}
        </v-alert>

        <v-card variant="outlined" class="pa-4 mt-3">
          <div class="d-flex align-center mb-2">
            <img
              src="@/assets/vevo/mascot_peek.png"
              alt="VEVO"
              class="mr-1"
              style="width: 24px; height: 24px; object-fit: contain;"
            />
            <span class="text-subtitle-2 font-weight-bold">{{ $t('aiReport.detail.contentTitle') }}</span>
            <v-spacer />
            <span class="text-caption text-grey" v-if="detail.content">
              {{ $t('aiReport.detail.charCount', { count: contentLength }) }}
            </span>
          </div>

          <div v-if="detail.content" class="ai-report-content text-body-2">
            {{ detail.content }}
          </div>
          <v-alert v-else type="info" variant="outlined" density="compact">
            {{ $t('aiReport.detail.notGenerated') }}
          </v-alert>
        </v-card>

        <div class="d-flex flex-column mt-3" style="gap: 0.5rem;">
          <v-btn
            v-if="detail.status === 'draft'"
            color="primary"
            variant="flat"
            block
            :loading="isPublishing"
            @click="setStatus('published')"
          >
            <v-icon start>mdi-publish</v-icon>
            {{ $t('aiReport.detail.publish') }}
          </v-btn>
          <v-btn
            v-if="detail.status === 'published'"
            variant="outlined"
            block
            :loading="isPublishing"
            @click="setStatus('draft')"
          >
            <v-icon start>mdi-undo</v-icon>
            {{ $t('aiReport.detail.revertToDraft') }}
          </v-btn>
          <v-tooltip
            v-if="detail.status === 'draft'"
            :disabled="!noData"
            location="top"
            :text="$t('aiReport.detail.noDataTooltip')"
          >
            <template v-slot:activator="{ props }">
              <span v-bind="props" class="d-block">
                <v-btn
                  variant="outlined"
                  block
                  :disabled="noData || isRegenerating"
                  @click="openRegenerate"
                >
                  <v-icon start>mdi-refresh</v-icon>
                  {{ $t('aiReport.detail.regenerate') }}
                </v-btn>
              </span>
            </template>
          </v-tooltip>
        </div>

        <v-alert
          v-if="isRegenerating && !regenerateOpen"
          variant="outlined"
          color="primary"
          icon="mdi-progress-clock"
          density="compact"
          class="mt-3"
        >
          <div class="text-body-2 mb-1">{{ $t('aiReport.detail.regeneratingInProgress') }}</div>
          <v-progress-linear indeterminate color="primary" height="6" rounded />
        </v-alert>

        <v-card variant="outlined" class="pa-3 mt-3">
          <div class="text-caption text-grey-darken-2 mb-1 font-weight-bold">{{ $t('aiReport.detail.operationLog') }}</div>
          <div class="text-caption">
            {{ $t('aiReport.detail.lastGenerated', { date: formatDateTime(detail.lastGeneratedAt) }) }}
            <span v-if="detail.lastGeneratedBy" class="text-grey"> / by {{ detail.lastGeneratedBy }}</span>
          </div>
          <div class="text-caption">
            {{ $t('aiReport.detail.lastPublished', { date: formatDateTime(detail.lastPublishedAt) }) }}
            <span v-if="detail.lastPublishedBy" class="text-grey"> / by {{ detail.lastPublishedBy }}</span>
          </div>
        </v-card>

        <v-alert
          v-if="detail.status === 'published'"
          type="warning"
          variant="outlined"
          density="compact"
          class="mt-3 text-caption"
        >
          {{ $t('aiReport.detail.publishedNotice') }}
        </v-alert>
      </div>
    </v-main>

    <v-dialog v-model="regenerateOpen" max-width="420" persistent>
      <v-card class="pa-4">
        <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('aiReport.regenerateDialog.title') }}</div>
        <p class="text-body-2">
          {{ $t('aiReport.regenerateDialog.description', { name: detail ? `${detail.lastName} ${detail.firstName}` : '' }) }}
        </p>
        <v-alert variant="outlined" color="primary" icon="mdi-information-outline" density="compact" class="my-2">
          <div class="text-caption">
            <template v-if="regenerateMode === 'translate'">{{ $t('aiReport.regenerateDialog.translateNotice') }}</template>
            {{ $t('aiReport.regenerateDialog.asyncNotice') }}
          </div>
        </v-alert>

        <template v-if="canTranslate">
          <v-radio-group
            v-model="regenerateMode"
            class="mb-1"
            density="compact"
            hide-details
            :disabled="!!activeBatch && activeBatch.status === 'processing'"
          >
            <v-radio :label="$t('aiReport.regenerateDialog.modeSimple')" value="simple" />
            <v-radio :label="$t('aiReport.regenerateDialog.modeTranslate')" value="translate" />
          </v-radio-group>

          <v-select
            v-if="regenerateMode === 'translate'"
            v-model="selectedLanguage"
            :items="languageItems"
            item-title="label"
            item-value="code"
            :label="$t('aiReport.regenerateDialog.targetLanguage')"
            variant="outlined"
            density="compact"
            hide-details
            class="mb-2"
            :disabled="!!activeBatch && activeBatch.status === 'processing'"
          />
        </template>

        <div v-if="activeBatch" class="my-2">
          <v-progress-linear
            :indeterminate="activeBatch.status === 'processing'"
            :model-value="activeBatch.status === 'completed' ? 100 : 0"
            color="primary"
            height="8"
            rounded
          />
          <div class="mt-1 text-body-2">
            <template v-if="activeBatch.status === 'processing'">{{ $t('aiReport.regenerateDialog.processing') }}</template>
            <template v-else-if="activeBatch.status === 'completed'">
              <v-icon size="small" color="success">mdi-check-circle</v-icon>
              {{ $t('aiReport.regenerateDialog.completed') }}
            </template>
            <template v-else-if="activeBatch.status === 'failed'">
              <v-icon size="small" color="red">mdi-alert-circle</v-icon>
              {{ $t('aiReport.regenerateDialog.failed') }}
            </template>
          </div>
        </div>

        <div class="d-flex mt-2" style="gap: 0.5rem;">
          <v-spacer />
          <v-btn variant="text" @click="closeRegenerate" :disabled="isStarting">
            {{ activeBatch && activeBatch.status === 'completed' ? $t('common.close') : $t('common.cancel') }}
          </v-btn>
          <v-btn
            v-if="!activeBatch || activeBatch.status === 'failed'"
            color="primary"
            variant="flat"
            :loading="isStarting"
            :disabled="startRegenerateDisabled"
            @click="startRegenerate"
          >
            {{ regenerateMode === 'translate' ? $t('aiReport.regenerateDialog.translate') : $t('aiReport.regenerateDialog.start') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import AiReportStatusChip from "@/components/molecules/AiReportStatusChip.vue";
import { aiReportModule } from "@/store/dataModules/aiReportModule";
import { AI_REPORT_LANGUAGE_CODES } from "@/api/ApiAiReport";
import { i18n } from "@/plugins/i18n";
import type { AiReportLanguage, AiReportQuarter } from "@/api/ApiAiReport";

type RegenerateMode = "simple" | "translate";

interface DataType {
  year: number;
  quarter: AiReportQuarter;
  childId: number;
  isFetching: boolean;
  isPublishing: boolean;
  isStarting: boolean;
  regenerateOpen: boolean;
  regenerateMode: RegenerateMode;
  selectedLanguage: AiReportLanguage | null;
  pollTimer: number | null;
}

export default defineComponent({
  components: { AiReportStatusChip },
  data: (): DataType => ({
    year: 0,
    quarter: 1 as AiReportQuarter,
    childId: 0,
    isFetching: false,
    isPublishing: false,
    isStarting: false,
    regenerateOpen: false,
    regenerateMode: "simple",
    selectedLanguage: null,
    pollTimer: null,
  }),
  computed: {
    detail() {
      return aiReportModule.detail;
    },
    activeBatch() {
      return aiReportModule.activeBatch;
    },
    contentLength(): number {
      return this.detail && this.detail.content ? this.detail.content.length : 0;
    },
    noData(): boolean {
      return !!this.detail && this.detail.dataCoverageMonths.length === 0;
    },
    isRegenerating(): boolean {
      return !!this.activeBatch && this.activeBatch.status === "processing";
    },
    languageItems(): { code: AiReportLanguage; label: string }[] {
      return AI_REPORT_LANGUAGE_CODES.map((code) => ({
        code,
        label: i18n.global.t(`aiReport.languages.${code}`),
      }));
    },
    canTranslate(): boolean {
      return !!this.detail && this.detail.status === "draft" && !!this.detail.content;
    },
    startRegenerateDisabled(): boolean {
      return this.regenerateMode === "translate" && this.selectedLanguage == null;
    },
  },
  watch: {
    "$route.params": {
      handler() {
        this.readRoute();
        this.load();
      },
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  async mounted() {
    this.readRoute();
    await this.load();
    await this.resumeRegenerateIfRunning();
  },
  methods: {
    readRoute() {
      this.year = parseInt(this.$route.params.businessYear as string, 10);
      this.quarter = parseInt(this.$route.params.quarter as string, 10) as AiReportQuarter;
      this.childId = parseInt(this.$route.params.childId as string, 10);
    },
    regenBatchKey(): string {
      return `ai_report_regen_batch_${this.childId}_${this.year}_${this.quarter}`;
    },
    saveRegenBatchId(batchId: number): void {
      try {
        window.localStorage.setItem(this.regenBatchKey(), String(batchId));
      } catch (e) {
        return;
      }
    },
    readRegenBatchId(): number | null {
      try {
        const v = window.localStorage.getItem(this.regenBatchKey());
        if (!v) return null;
        const n = parseInt(v, 10);
        return Number.isNaN(n) ? null : n;
      } catch (e) {
        return null;
      }
    },
    clearRegenBatchId(): void {
      try {
        window.localStorage.removeItem(this.regenBatchKey());
      } catch (e) {
        return;
      }
    },
    async resumeRegenerateIfRunning() {
      const batchId = this.readRegenBatchId();
      if (batchId == null) return;
      try {
        const batch = await aiReportModule.pollBatch({ batchId });
        if (batch && batch.status === "processing") {
          this.startPolling(batchId);
        } else {
          this.clearRegenBatchId();
          aiReportModule.setActiveBatch(null);
        }
      } catch (e) {
        return;
      }
    },
    async load() {
      if (!this.year || !this.quarter || !this.childId) return;
      this.isFetching = true;
      try {
        await aiReportModule.fetchDetail({
          businessYear: this.year,
          quarter: this.quarter,
          childId: this.childId,
        });
      } finally {
        this.isFetching = false;
      }
    },
    async setStatus(status: "published" | "draft") {
      if (!this.detail) return;
      const name = `${this.detail.lastName} ${this.detail.firstName}`;
      const isPublish = status === "published";
      const result = await Swal.fire({
        title: i18n.global.t(isPublish ? "aiReport.confirm.publishTitle" : "aiReport.confirm.revertTitle", { name }),
        html: i18n.global.t(isPublish ? "aiReport.confirm.publishMessage" : "aiReport.confirm.revertMessage"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: i18n.global.t(isPublish ? "aiReport.detail.publish" : "aiReport.detail.revertToDraft"),
        cancelButtonText: i18n.global.t("common.cancel"),
        allowOutsideClick: false,
      });
      if (!result.isConfirmed) return;
      this.isPublishing = true;
      try {
        await aiReportModule.updateStatus({
          businessYear: this.year,
          quarter: this.quarter,
          childId: this.childId,
          status,
        });
      } finally {
        this.isPublishing = false;
      }
    },
    openRegenerate() {
      aiReportModule.setActiveBatch(null);
      this.regenerateMode = "simple";
      this.selectedLanguage = null;
      this.regenerateOpen = true;
    },
    closeRegenerate() {
      this.regenerateOpen = false;
      this.stopPolling();
      aiReportModule.setActiveBatch(null);
    },
    async startRegenerate() {
      this.isStarting = true;
      try {
        const language =
          this.regenerateMode === "translate" ? this.selectedLanguage : null;
        const batch = await aiReportModule.regenerate({
          businessYear: this.year,
          quarter: this.quarter,
          childId: this.childId,
          language,
        });
        if (batch) {
          this.saveRegenBatchId(batch.batchId);
          this.startPolling(batch.batchId);
        }
      } finally {
        this.isStarting = false;
      }
    },
    startPolling(batchId: number) {
      this.stopPolling();
      this.pollTimer = window.setInterval(async () => {
        const batch = await aiReportModule.pollBatch({ batchId });
        if (batch && (batch.status === "completed" || batch.status === "failed")) {
          this.stopPolling();
          this.clearRegenBatchId();
          await this.load();
        }
      }, 1500);
    },
    stopPolling() {
      if (this.pollTimer !== null) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    goBack() {
      this.$router.push(`/ai-report/quarter/${this.year}/${this.quarter}`);
    },
    formatDateTime(value: string | null): string {
      if (!value) return "—";
      const locale = i18n.global.locale.value.toString().toLowerCase();
      // JA: YYYY/MM/DD, EN/VI: DD/MM/YYYY (English/European style)
      const pattern = locale === "ja" || locale.startsWith("ja-")
        ? "YYYY/MM/DD HH:mm"
        : "DD/MM/YYYY HH:mm";
      return dayjs(value).format(pattern);
    },
    formatCoverage(months: number[]): string {
      if (months.length === 0) return i18n.global.t("aiReport.detail.coverageNone");
      return i18n.global.t("aiReport.detail.coverageMonths", { months: months.join(", ") });
    },
  },
});
</script>

<style lang="scss" scoped>
.ai-report-content {
  white-space: pre-wrap;
  line-height: 1.7;
}
</style>
